import React, { useState } from 'react';
import { renderApptAddressGroup, SelectAutocompleteMultiple } from 'components';
import { Images } from 'utils';

const LOCK_ICON_FILTER =
    'brightness(0) saturate(100%) invert(68%) sepia(9%) saturate(499%) hue-rotate(179deg) brightness(93%) contrast(93%)';

/**
 * The Template field of a note form, following the rules of the appointment's service
 * (`serviceTemplate` from `getServiceTemplate`). The service's first template is the default:
 * - one locked template: shown, not changeable;
 * - several locked templates: a select offering only those, with a lock hint under it;
 * - linked but not locked: the default is shown with an edit button, which opens every template,
 *   the service's listed first;
 * - none: a select offering every published template.
 */
export const NoteTemplateField = ({ serviceTemplate, info, forms, control, loadType, onSelect, onClear }) => {
    const [showSelectInput, setShowSelectInput] = useState(false);
    const templates = serviceTemplate?.templates || [];
    const locked = !!serviceTemplate?.onlyUse;

    if (templates.length === 1 && locked) {
        return (
            <div className="only-use-wrapper">
                <div>
                    <p className="only-use-template">Template*</p>
                    <p className="only-use-template-title">{templates[0].title}</p>
                    <p className="only-use-template-info">(This template is required by the service)</p>
                </div>
                <img src={Images.lock} alt="lock" style={{ filter: LOCK_ICON_FILTER, width: 20, height: 20 }} />
            </div>
        );
    }

    if (templates.length && !locked && !showSelectInput && !info?.id) {
        return (
            <div className="recomend-use-wrapper">
                <div>
                    <p className="only-use-template">Template*</p>
                    <p className="only-use-template-title">{templates[0].title}</p>
                    <p className="only-use-template-info">(auto-selected from service)</p>
                </div>
                <button type="button" className="reset-btn-by-default" onClick={() => setShowSelectInput(true)}>
                    <img src={Images.edit} alt="edit" />
                </button>
            </div>
        );
    }

    // An existing note keeps its template (the select is disabled), so only a new one follows the service.
    const templateIds = info?.id ? [] : templates.map((template) => template.id);
    const serviceForms = templateIds.map((id) => forms?.find((form) => form?.id === id)).filter(Boolean);
    const otherForms = forms?.filter((form) => !templateIds.includes(form?.id)) || [];
    const options = !templateIds.length ? forms : locked ? serviceForms : [...serviceForms, ...otherForms];
    const grouped = !!templateIds.length && !locked;
    // Nothing else on screen says why the list is short, so say it under the field.
    const lockedList = !!templateIds.length && locked;

    const select = (
        <SelectAutocompleteMultiple
            disabled={!!info?.id}
            defaultSelected={info?.template && { title: info?.template?.title }}
            handleChange={(value) => (value?.id ? onSelect(value.id) : onClear())}
            loadType={loadType}
            name={`customForm`}
            placeholder={lockedList ? 'Select a template required by the service' : 'Select Template'}
            label={'Template*'}
            control={control}
            options={options}
            groupBy={grouped ? (option) => (templateIds.includes(option?.id) ? 'Service templates' : 'Other templates') : undefined}
            renderGroup={grouped ? renderApptAddressGroup : undefined}
            rules={{ required: 'Template is required.' }}
            renderValue={(option) => `${option?.title} ${option?.description ? `- ${option?.description}` : ''}`}
        />
    );

    if (!lockedList) return select;
    return (
        <div className="locked-templates-field">
            {select}
            <div className="locked-templates-hint">
                <img src={Images.lock} alt="lock" style={{ filter: LOCK_ICON_FILTER, width: 14, height: 14 }} />
                <p className="only-use-template-info">Only templates required by the service are listed</p>
            </div>
        </div>
    );
};
